import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { useSNAP } from "hooks/useSNAP";

// Cache global de imágenes de API (TTL = 10 min)
const imageCache = new Map();
const TTL = 10 * 60 * 1000;

const getCachedImage = (id) => {
  const entry = imageCache.get(id);
  if (!entry) return null;

  const now = Date.now();
  if (now - entry.timestamp > TTL) {
    URL.revokeObjectURL(entry.blobUrl);
    imageCache.delete(id);
    return null;
  }
  return entry.blobUrl;
};

const setCachedImage = (id, blob) => {
  const blobUrl = URL.createObjectURL(blob);
  imageCache.set(id, { blobUrl, timestamp: Date.now() });
  return blobUrl;
};

export const CTImage = ({
  caption = "",
  funcion = "",
  metodo = "GET",
  id = null,
  extraParams = {},
  usePublic = false,          // 👉 NUEVA PROP
  publicPath = "/bobinas",   // 👉 Ruta en /public
  width = "w-full",
  height = "h-auto",
  borderRadius = "rounded-lg",
  boxShadow = "shadow-md",
  bordered = true,
  className = "",
  style = {},
  ...rest
}) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const snap = useSNAP({ useUserId: true, useToken: true });
  const memoParams = useMemo(() => extraParams, [JSON.stringify(extraParams)]);

  const loadImage = useCallback(async () => {
    if (id === null) return;

    // 👉 Si queremos usar imágenes de /public, no hacemos fetch
    if (usePublic) {
      setImgSrc(`${publicPath}/${id}`);
      setLoading(false);
      setError(null);
      return;
    }

    // --- FLUJO ORIGINAL (API) ---
    if (!funcion) return;

    const cached = getCachedImage(id);
    if (cached) {
      setImgSrc(cached);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const queryString = funcion.split("?")[1] || "";
      const params = new URLSearchParams(queryString);

      // Obtener metode o default "getimage"
      const metode = params.get("metode") || "getimage";

      // Setear valores
      snap.setFuncion(funcion);
      snap.setMetodo(metodo);
      snap.setParam("metode", metode);
      snap.setParam("id", id);



      Object.entries(memoParams).forEach(([key, value]) =>
        snap.setParam(key, value)
      );

      const res = await snap.fetchData();

      if (res?.Blob && res.ContentType?.startsWith("image/")) {
        const blobUrl = setCachedImage(id, res.Blob);
        setImgSrc(blobUrl);
      } else {
        setError("El endpoint no devolvió una imagen válida.");
      }
    } catch (err) {
      console.error("CTImage error:", err);
      setError("Error al cargar la imagen.");
    } finally {
      setLoading(false);
    }
  }, [funcion, metodo, id, memoParams, usePublic, publicPath, snap]);

  useEffect(() => {
    loadImage();
  }, [loadImage]);

  // Liberar blob cuando se desmonte
  useEffect(() => {
    return () => {
      if (imgSrc && !usePublic && !getCachedImage(id)) {
        URL.revokeObjectURL(imgSrc);
      }
    };
  }, [imgSrc, id, usePublic]);

  const classes = clsx(
    "ctimage flex p-4 flex-col items-center justify-center bg-white",
    bordered && "border border-gray-200",
    borderRadius,
    boxShadow,
    width,
    height,
    className
  );

  return (
  <div className={classes} style={style} {...rest}>
    {/* Zona de contenido principal */}
    {loading && !usePublic && (
      <div className="flex flex-col items-center justify-center p-4 text-gray-500 w-full h-[250px]">
        <Loader2 className="h-6 w-6 animate-spin mb-2 text-blue-600" />
        <span className="text-sm">Cargando imagen...</span>
      </div>
    )}

    {!loading && error && (
      <div className="flex items-center justify-center text-red-500 text-sm p-4 w-full h-[250px]">
        {error}
      </div>
    )}

    {!loading && !error && imgSrc && (
      <img
        src={imgSrc}
        alt={caption || "imagen"}
        className="max-w-full max-h-[500px] object-contain rounded-md"
      />
    )}

    {/* Si no hay imagen cargada, mostramos un placeholder opcional */}
    {!loading && !error && !imgSrc && (
      <div className="flex items-center justify-center text-gray-400 w-full h-[250px] border border-dashed rounded-md">
        Imagen no disponible
      </div>
    )}

    {/* El caption siempre al final */}
    {caption && (
      <div className="mt-2 text-sm font-medium text-gray-700 text-center">
        {caption}
      </div>
    )}
  </div>
);
};

CTImage.propTypes = {
  caption: PropTypes.string,
  funcion: PropTypes.string,
  metodo: PropTypes.oneOf(["GET", "POST"]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  extraParams: PropTypes.object,
  usePublic: PropTypes.bool,          // 👉 Nuevo
  publicPath: PropTypes.string,       // 👉 Nuevo
  width: PropTypes.string,
  height: PropTypes.string,
  borderRadius: PropTypes.string,
  boxShadow: PropTypes.string,
  bordered: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default CTImage;
